import { CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Table } from 'antd'
import Divider from 'antd/lib/divider'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import Highlighter from 'react-highlight-words'
import { ItemResponseNodeBeanObject, ListPagination, ListResponseNodeBean, NodeBean } from 'src/api/api'
import { filterFunc, getFilterData, sorterFunc, useActionsMediaQuery, withActionsMediaQuery } from 'src/util/Util'
import { StorageKey, storageReaderFactory, storageWriterFactory } from '../../../util/Storage'
import { useDebounce } from '../../../util/useDebounce'
import nodeApi from '../node/NodeApi'
import './style.css'

const Column = Table.Column

const storageWriter = storageWriterFactory('node')
const storageReader = storageReaderFactory('node')

const NodeList = () => {

    const [searchQuery, setSearchQuery] = useState('')
    const [data, setData] = useState<NodeBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [isFiltered, setIsFiltered] = useState(false)

    const areActionsVertical = useActionsMediaQuery('800px')

    const loadData = (page) => {
        nodeApi
            .list(pagination.defaultPageSize, page, searchQuery)
            .then((listResponseNodeBean: ListResponseNodeBean) => {

                const array = listResponseNodeBean.data as Array<ItemResponseNodeBeanObject>
                const nodeBeans: NodeBean[] = array.map(item => item.data as NodeBean)

                if (nodeBeans.length == 0 && page > 1) {
                    loadData(page - 1)
                } else {
                    const pageResponse: ListPagination = listResponseNodeBean.pagination as ListPagination
                    storageWriter.write(StorageKey.SearchParams, {
                        page: pageResponse.currentPage
                    })

                    setData(nodeBeans)
                    setPagination({
                        ...pagination,
                        total: pageResponse.total,
                        current: pageResponse.currentPage
                    })
                }
            })
    }

    const initialLoad = () => {
        let searchParams = storageReader.read(StorageKey.SearchParams)
        let page = searchParams.page || pagination.current

        loadData(page)
    }

    useEffect(() => {
        initialLoad()
    }, [])

    const deleteNode = (record: NodeBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: () => {
                nodeApi.delete(record.id as number).then(() => {
                    let page = pagination.current
                    if (data.length == 1 && pagination.current > 1) {
                        page -= 1
                    }
                    loadData(page)
                })
            }
        })
    }

    let handleReset = () => {
        setSearchQuery('')
        if (isFiltered) {
            loadData(1)
            setIsFiltered(true)
        }
    }

    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    useEffect(() => {
        loadData(1)
        setIsFiltered(true)
    }, [debouncedSearchQuery])

    return (
        <div>
            <div style={{ display: 'flex' }}>
                <div className="c-l-search-buttons">
                    <Input
                        addonAfter={<CloseOutlined onClick={() => handleReset()} />}
                        placeholder="Node search"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value)
                        }}
                    />
                </div>
            </div>
            <Table
                rowKey="id"
                size="middle"
                pagination={{
                    ...pagination,
                    onChange: (page: number) => loadData(page)
                }}
                dataSource={data}>
                <Column key="id" title="ID" dataIndex="id" />
                <Column key="name" title="Name" dataIndex="name" filterMultiple
                    filters={getFilterData(data, bean => bean.name)}
                    onFilter={filterFunc(bean => bean.name)}
                    sorter={sorterFunc(bean => bean.name)}
                    render={text => searchQuery !== '' ?
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[searchQuery]}
                            autoEscape
                            textToHighlight={text.toString()} /> :
                        text}
                />
                <Column key="clusterId" title="Cluster ID list" dataIndex="clusterId" render={list => list.join(',')} />
                <Column key="ip" title="IP" dataIndex="ip" />
                <Column key="port" title="Port" dataIndex="port" />
                <Column align={'center'} key="action" title="Actions" render={(text: string, record: NodeBean) => (
                    <span>
                        <Link to={`/node/${record.id}/edit`}>
                            <Button htmlType="button" title="Edit">
                                <EditOutlined />
                            </Button>
                        </Link>
                        <Divider className="actions-horisontable-divider"
                            type={areActionsVertical ? 'horizontal' : 'vertical'} />
                        <Button
                            onClick={() => deleteNode(record)}
                            type="primary"
                            htmlType="button"
                            danger
                            title="Delete">
                            <DeleteOutlined />
                        </Button>
                    </span>
                )} />
            </Table>
            <Link to={`/node/create`}>
                <Button htmlType="button" type="primary">Create new</Button>
            </Link>
        </div>
    )
}

export default withActionsMediaQuery('550px')(NodeList)
